import React from 'react';
import { Grid, Typography, TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText, Chip, Box, Button, IconButton } from '@mui/material';
import { Field, type FormikErrors, type FormikTouched } from 'formik';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { STATUS_OPTIONS } from '../../constants/visitorConstants';
import { TenantSelectIfAdmin } from '../../../../shared/components/SelectsDinamycs/TenantSelectIfAdmin';
import { CloudUpload, Delete, Image } from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

interface FormContentProps {
  values: any;
  errors: FormikErrors<any>;
  touched: FormikTouched<any>;
  isSubmitting: boolean;
  setFieldValue: (field: string, value: any) => void;
  userOptions: Array<{ value: string; label: string }>;
  areaOptions: Array<{ value: string; label: string }>;
}

export function FormContent({
  values,
  errors,
  touched,
  isSubmitting,
  setFieldValue,
  userOptions,
  areaOptions,
}: FormContentProps) {
  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setFieldValue('images', [...(values.images || []), ...files]);
    }
  };

  const removeImage = (index: number) => {
    const newImages = [...(values.images || [])];
    newImages.splice(index, 1);
    setFieldValue('images', newImages);
  };

  return (
    <LocalizationProvider 
      dateAdapter={AdapterDayjs} 
      adapterLocale="pt-br"
      localeText={{
        fieldDayPlaceholder: () => 'DD',
        fieldMonthPlaceholder: () => 'MM',
        fieldYearPlaceholder: () => 'AAAA',
        fieldHoursPlaceholder: () => 'HH',
        fieldMinutesPlaceholder: () => 'mm',
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography fontWeight={600} fontSize={16}>
            Dados do Visitante
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Field
            as={TextField}
            fullWidth
            label="Nome"
            name="name"
            required
            disabled={isSubmitting}
            error={!!(touched.name && errors.name)}
            helperText={touched.name && errors.name}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Field
            as={TextField}
            fullWidth
            label="CPF"
            name="document"
            placeholder="000.000.000-00"
            required
            disabled={isSubmitting}
            error={!!(touched.document && errors.document)}
            helperText={touched.document && errors.document}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Field
            as={TextField}
            fullWidth
            label="Telefone"
            name="phone_number"
            placeholder="(00) 00000-0000"
            required
            disabled={isSubmitting}
            error={!!(touched.phone_number && errors.phone_number)}
            helperText={touched.phone_number && errors.phone_number}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!(touched.status_flag && errors.status_flag)}>
            <InputLabel>Status</InputLabel>
            <Select
              value={values.status_flag}
              onChange={(e) => setFieldValue('status_flag', e.target.value)}
              disabled={isSubmitting}
            >
              {STATUS_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {touched.status_flag && errors.status_flag && (
              <FormHelperText>{String(errors.status_flag)}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography fontWeight={600} fontSize={16} sx={{ mt: 2 }}>
            Dados do Acesso
          </Typography>
        </Grid>



        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!(touched.user_id && errors.user_id)} required>
            <InputLabel required>Usuário Responsável *</InputLabel>
            <Select
              value={values.user_id || ''}
              onChange={(e) => setFieldValue('user_id', e.target.value)}
              disabled={isSubmitting}
              required
            >
              <MenuItem value="">
                <em>Selecione um usuário</em>
              </MenuItem>
              {userOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {touched.user_id && errors.user_id && (
              <FormHelperText>{typeof errors.user_id === 'string' ? errors.user_id : String(errors.user_id)}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <DateTimePicker
            label="Data de Início"
            value={values.start_date ? dayjs(values.start_date) : null}
            onChange={(newValue) => setFieldValue('start_date', newValue?.toISOString())}
            disabled={isSubmitting}
            format="DD/MM/YYYY HH:mm"
            ampm={false}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!(touched.start_date && errors.start_date),
                helperText: touched.start_date && typeof errors.start_date === 'string' ? errors.start_date : undefined,
                placeholder: "DD/MM/AAAA HH:mm"
              },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <DateTimePicker
            label="Data de Fim"
            value={values.end_date ? dayjs(values.end_date) : null}
            onChange={(newValue) => setFieldValue('end_date', newValue?.toISOString())}
            disabled={isSubmitting}
            format="DD/MM/YYYY HH:mm"
            ampm={false}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!(touched.end_date && errors.end_date),
                helperText: touched.end_date && typeof errors.end_date === 'string' ? errors.end_date : undefined,
                placeholder: "DD/MM/AAAA HH:mm"
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={!!(touched.area_id && errors.area_id)}>
            <InputLabel>Áreas de Acesso</InputLabel>
            <Select
              multiple
              value={values.area_id || []} 
              onChange={(e) => {
                console.log('Select onChange - valor:', e.target.value);
                console.log('Select onChange - tipo:', typeof e.target.value);
                setFieldValue('area_id', e.target.value);
              }}
              disabled={isSubmitting}
              required
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected || []).map((value: string) => {
                    const area = areaOptions.find(option => option.value === value);
                    return (
                      <Chip key={value} label={area?.label || value} size="small" />
                    );
                  })}
                </Box>
              )}
            >
              {areaOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {touched.area_id && errors.area_id && (
              <FormHelperText>{typeof errors.area_id === 'string' ? errors.area_id : String(errors.area_id)}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography fontWeight={600} fontSize={16} sx={{ mt: 2, mb: 2 }}>
            Imagens do Visitante (Opcional)
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ 
            border: '2px dashed #e0e0e0', 
            borderRadius: 2, 
            p: 3,
            textAlign: 'center',
            backgroundColor: '#fafafa',
            '&:hover': {
              borderColor: '#1976d2',
              backgroundColor: '#f5f5f5'
            }
          }}>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: 'none' }}
              id="image-upload"
              disabled={isSubmitting}
            />
            <label htmlFor="image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                disabled={isSubmitting}
                sx={{ mb: 1 }}
              >
                Selecionar Imagens
              </Button>
            </label>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Formatos aceitos: JPG, PNG, GIF • Máximo de 5MB por imagem
            </Typography>
          </Box>

          {/* Preview das imagens selecionadas */}
          {values.images && values.images.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Imagens selecionadas ({values.images.length}):
              </Typography>
              <Grid container spacing={2}>
                {values.images.map((file: File, index: number) => (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Box sx={{ 
                      position: 'relative',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 1,
                      backgroundColor: '#fff'
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                      }}>
                        <Image fontSize="small" color="primary" />
                        <Typography variant="caption" sx={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1
                        }}>
                          {file.name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeImage(index)}
                          disabled={isSubmitting}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {touched.images && errors.images && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {typeof errors.images === 'string' ? errors.images : String(errors.images)}
            </Typography>
          )}
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
}